import { withRequestDeadline } from '~/utils/requestDeadline';
import type { ROUTES } from '~/utils/routes';
import { getData, patchData, postData } from '~/utils/service';
import { MARKET_ACTION_TIMEOUT_MS, marketReadTimeout } from '../requestPolicy';
import { beginNegotiation, BeginNegotiationInput } from './beginNegotiation';
import {
  DraftSubmitResult,
  NegotiationFormValues,
  NegotiationResponseType,
  OnAcceptOfferEvent,
  OnCalculateEvent,
  OnCounterOfferEvent,
  OnRejectOfferEvent,
  OnSubmitDraftEvent,
  OnWithdrawOfferEvent,
  Scope,
} from './types';
import {
  NegotiationDraftApiResponse,
  transformDraftTermsToApi,
  transformFormValuesToApi,
  transformNegotiationsApiToFormValues,
} from './utils';

const ROUTE_NAME = 'msaasNegotiation';
const ROUTE_NAME_NEGOTIATIONS = 'msaasNegotiations';
const ROUTE_NAME_BY_RESPONSE = 'msaasNegotiationByResponse';
const ROUTE_NAME_CALCULATIONS = 'msaasNegotiationCalculations';

type RouteName = keyof typeof ROUTES;

const getRouteNames = (
  responseType: NegotiationResponseType
): { byResponse: RouteName; negotiations: RouteName; calculations: RouteName } => ({
  byResponse: responseType === 'job' ? 'jobNegotiationByResponse' : ROUTE_NAME_BY_RESPONSE,
  negotiations: responseType === 'job' ? 'jobNegotiations' : ROUTE_NAME_NEGOTIATIONS,
  calculations: responseType === 'job' ? 'jobNegotiationCalculations' : ROUTE_NAME_CALCULATIONS,
});

const closeNegotiationResponse = async (
  context: Scope['context'],
  negotiationId: string,
  status: 'REJECTED' | 'WITHDRAWN'
) => {
  const closedNegotiation = context.negotiations?.find((n) => n.id === parseInt(negotiationId));

  if (!closedNegotiation?.jobResponse) {
    throw new Error(`Job response not found for negotiation ${negotiationId}`);
  }
  return patchData('jobOpportunityStatus', {
    pathParams: { responseId: closedNegotiation.jobResponse.id.toString() },
    data: { status },
  });
};

export const API_CALLS = {
  get: async ({ input }: { input: Scope }): Promise<NegotiationFormValues[]> => {
    return await getData(ROUTE_NAME, {
      pathParams: { employerId: input.context.employerUser?.employerId?.toString() || '' },
      transformResponse: (data) => transformNegotiationsApiToFormValues(data, 'msaas'),
    });
  },
  /**
   * Load the negotiation chain scoped to a single JobResponse.
   *
   * Backed by the response-scoped JOB or MSaaS endpoint, which is
   * guarded by `is_job_response_owner` server-side and returns the chain
   * ordered by version DESC with listing + employer eager-loaded.
   */
  getByResponseId: async ({
    input,
    signal,
  }: {
    input: Scope;
    signal?: AbortSignal;
  }): Promise<NegotiationFormValues[]> => {
    const responseId = input.context.responseId;
    if (responseId === undefined || responseId === null) {
      throw new Error('responseId is required to load negotiations');
    }
    const responseType = input.context.responseType;
    return await withRequestDeadline(
      (requestSignal) =>
        getData<NegotiationFormValues[]>(getRouteNames(responseType).byResponse, {
          signal: requestSignal,
          pathParams: { responseId: responseId.toString() },
          transformResponse: (data: string) =>
            transformNegotiationsApiToFormValues(data, responseType),
        }),
      marketReadTimeout(input.context.recoveryDeadlineAt),
      signal
    );
  },
  calculate: async ({ input }: { input: Scope }) => {
    const data = await postData(getRouteNames(input.context.responseType).calculations, {
      data: (input.event as OnCalculateEvent).payload.values,
      transformRequest: transformFormValuesToApi,
    });
    return data;
  },
  // [MCV-1850] The listing owner declines (REJECTED) and the responder withdraws
  // (WITHDRAWN); the server enforces which party may set which.
  rejectNegotiation: async ({ input }: { input: Scope }) => {
    const event = input.event as OnRejectOfferEvent;
    return closeNegotiationResponse(input.context, event.payload.id, 'REJECTED');
  },
  withdrawNegotiation: async ({ input }: { input: Scope }) => {
    const event = input.event as OnWithdrawOfferEvent;
    return closeNegotiationResponse(input.context, event.payload.id, 'WITHDRAWN');
  },
  acceptNegotiation: async ({ input }: { input: Scope }) => {
    const event = input.event as OnAcceptOfferEvent;
    const acceptedNegotiation = event.data;
    if (!acceptedNegotiation?.jobResponse) {
      throw new Error(`Job response not found for negotiation ${event.data.id}`);
    }

    const data = await postData('hlxAccept', {
      pathParams: { responseId: acceptedNegotiation.jobResponse.id.toString() },
      data: {
        expectedNegotiationId: acceptedNegotiation.id,
        ...(event.feeAuthorization ? { feeAuthorization: event.feeAuthorization } : {}),
      },
    });
    return data;
  },
  counterNegotiation: async ({ input }: { input: Scope }) => {
    const event = input.event as OnCounterOfferEvent;
    // Strip server-owned fields and FK relations; the backend creates a new
    // negotiation row, so these cannot be carried over from the prior version.
    const newNegotiation = {
      ...event.data,
    } as Partial<NegotiationFormValues> & Record<string, unknown>;
    delete newNegotiation.id;
    delete newNegotiation.msaasListing;
    delete newNegotiation.msaasListingDetails;
    delete newNegotiation.listingEmployer;
    delete newNegotiation.respondingEmployer;
    delete newNegotiation.jobListing;
    delete newNegotiation.jobListingDetail;
    delete newNegotiation.respondingProvider;
    delete newNegotiation.responseType;
    delete newNegotiation.senderParty;

    if (!event.data.jobResponse) {
      throw new Error(`Job response not found for negotiation ${event.data.id}`);
    }
    newNegotiation.jobResponse = {
      ...event.data.jobResponse,
      status: 'NEGOTIATING',
    };

    const data = await postData(getRouteNames(input.context.responseType).negotiations, {
      data: newNegotiation,
      transformRequest: transformFormValuesToApi,
    });

    return data;
  },
  /**
   * [MCV-1646] Load the draft for the negotiation editor's initial-offer flow.
   * Scoped by an existing JobResponse (`{ responseId: number }`) or a bare
   * listing (`{ listingType, listingId }`) -- see `NegotiationDraftSource`.
   */
  getDraft: async ({
    input,
    signal,
  }: {
    input: Scope;
    signal?: AbortSignal;
  }): Promise<NegotiationDraftApiResponse> => {
    const source = input.context.draftSource;
    if (!source) {
      throw new Error('draftSource is required to load a negotiation draft');
    }
    const listingType: 'JOB' | 'MSAAS' =
      'listingType' in source
        ? source.listingType
        : input.context.responseType === 'job'
          ? 'JOB'
          : 'MSAAS';
    const params =
      'responseId' in source
        ? { listingType, responseId: source.responseId.toString() }
        : {
            listingType,
            ...(listingType === 'JOB'
              ? { jobListingId: source.listingId.toString() }
              : { msaasListingId: source.listingId.toString() }),
          };

    return await withRequestDeadline(
      (requestSignal) =>
        getData<NegotiationDraftApiResponse>('jobResponseNegotiationDraft', {
          params,
          signal: requestSignal,
        }),
      marketReadTimeout(input.context.recoveryDeadlineAt),
      signal
    );
  },
  /**
   * [MCV-1646] Submit the first negotiation for a draft. When the draft is
   * scoped to an existing JobResponse, the response-scoped
   * `jobResponseInitialNegotiation` POST is used; otherwise the listing-level
   * `begin-negotiation` endpoint both creates the JobResponse and seeds v1.
   */
  submitDraft: async ({
    input,
    signal,
  }: {
    input: Scope;
    signal?: AbortSignal;
  }): Promise<DraftSubmitResult> => {
    const source = input.context.draftSource;
    if (!source) {
      throw new Error('draftSource is required to submit a negotiation draft');
    }
    const event = input.context.submittedDraft ?? (input.event as OnSubmitDraftEvent);
    const responseType = input.context.responseType;
    const negotiationTerms = transformDraftTermsToApi(event.data, responseType);
    const providerIds = event.providerIds ?? input.context.draftProviderIds;

    if ('responseId' in source) {
      return await withRequestDeadline(
        (requestSignal) =>
          postData<DraftSubmitResult>('jobResponseInitialNegotiation', {
            signal: requestSignal,
            pathParams: { responseId: source.responseId.toString() },
            data: { negotiationTerms },
          }),
        MARKET_ACTION_TIMEOUT_MS,
        signal
      );
    }

    const beginInput: BeginNegotiationInput =
      source.listingType === 'JOB'
        ? { listingType: 'JOB', jobListingId: source.listingId, negotiationTerms }
        : {
            listingType: 'MSAAS',
            msaasListingId: source.listingId,
            providerIds,
            negotiationTerms,
          };
    return await beginNegotiation(beginInput, signal);
  },
};
